import classNames from 'classnames';
import Clipboard from 'clipboard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FolderOpen1Icon, InfoCircleIcon } from 'tdesign-icons-react';
import { Button, Image, Loading, MessagePlugin, Popconfirm, Progress, Space, Tooltip } from 'tdesign-react';

import Fail from '@/assets/image/editor/aigc_image_fail.png';
import Empty from '@/assets/svg/empty3.png';
import { CheckCircle, Copy3, EditorDel, MissionIcon, Reload } from '@/components/icon';
import { BasicImageViewer } from '@/components/review/image';
import { AIGC_IMAGE_DIMENTION, AiImageModelType, AiImageReq, DEFAULT_AI_IMAGE_MODEL, Mode, ResourceHistory, Status, UrlInfo } from '@/interface/aigc';
import { UserResourceType } from '@/interface/library';
import { StoryLayout } from '@/interface/module';
import { deleteAIResource } from '@/services/aigc';
import { CgiErrorCode } from '@/services/cgi';
import { insertLibrary } from '@/services/library';
import { downloadImageWatermark } from '@/utils/downloadFile';

import styles from './index.module.less';

export interface HistoryItemProps {
  history: ResourceHistory;
  selectedImage: string;
  isHorizontal: boolean;
  model?: AiImageModelType;
  generateImages: (postData: AiImageReq) => void;
  handleSelect: (url: UrlInfo) => void;
  onDelete: (id: string) => void;
}

const ImageHistoryItem: React.FC<HistoryItemProps> = ({
  history,
  selectedImage,
  isHorizontal,
  generateImages,
  handleSelect,
  onDelete,
}) => {
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';
  const { status: historyStatus, urls, prompt, relateId, style = '', aiTxt2ImgReq = '' } = history;
  const validUrls = urls.filter(e => e.url).map(e => e.url);
  const loading = (historyStatus === Status.WAITING
    && !urls.some(e => (e.url && e.url !== Empty) || e.errMsg));

  const [progress, setProgress] = useState<number>(0);
  const isValid = (url: string) => url && url !== 'initUrl' && url !== Fail && url !== Empty;
  const isSelected = (url: string) => isValid(url) && url === selectedImage;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev) => {
          const random = Math.random();
          if (prev < 35) {
            return Math.min(prev + 8 + Math.floor(random * 8), 35);
          } if (prev < 65) {
            return Math.min(prev + 6 + Math.floor(random * 6), 65);
          } if (prev < 90) {
            return Math.min(prev + 5 + Math.floor(random * 5), 90);
          } if (prev < 99) {
            return Math.min(prev + Math.floor(random * 3), 99);
          }
          return prev;
        });
      }, 2000);
    } else {
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const aiImageReq: AiImageReq = useMemo(() => {
    try {
      return JSON.parse(aiTxt2ImgReq);
    } catch {
      return {
        text: prompt,
        ...(isHorizontal
          ? AIGC_IMAGE_DIMENTION[StoryLayout.LAYOUT_HORIZONTAL]
          : AIGC_IMAGE_DIMENTION[StoryLayout.LAYOUT_VERTICAL]),
        mode: Mode.TXT2IMG,
        model: DEFAULT_AI_IMAGE_MODEL,
        style,
      };
    }
  }, [aiTxt2ImgReq, style, prompt, isHorizontal]);

  const handleRegenerate = () => {
    generateImages({ ...aiImageReq });
  };

  const handleDelete = async () => {
    const res = await deleteAIResource({ relateIds: [relateId] });
    if (res.code !== CgiErrorCode.NO_ERROR || !res.data.hasDeleted) {
      MessagePlugin.error('删除失败，请稍后重试');
      return;
    }
    MessagePlugin.success('删除成功');
    onDelete(relateId);
  };

  const clickImage = (urlInfo: UrlInfo) => {
    const { url } = urlInfo;
    if (isValid(url)) {
      handleSelect(urlInfo);
    }
  };

  const saveLibrary = async (urlInfo: UrlInfo, e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    const { url, name } = urlInfo;
    const params = {
      type: UserResourceType.RESOURCE_TYPE_IMAGE,
      materialId: storyId,
      batchResource: [{ url, resourceName: name }],
    };
    const { code, message } = await insertLibrary(params);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error(message || '保存失败');
    } else {
      MessagePlugin.success('已将该图片保存到素材库');
    }
  };

  const copyBtnRef = useRef(null);

  useEffect(() => {
    if (copyBtnRef.current) {
      const clipboard = new Clipboard(copyBtnRef.current, {
        text: () => prompt,
      });

      clipboard.on('success', () => {
        MessagePlugin.success('已复制到剪贴板');
      });

      clipboard.on('error', () => {
        MessagePlugin.error('复制失败');
      });

      return () => {
        clipboard.destroy();
      };
    }
  }, [prompt]);

  return (
    <div className={styles.container}>
      <div className={styles.prompt}>{prompt}</div>
      <div className={styles.content}>
        <Loading
          showOverlay
          loading={loading}
          text="生成中，您也可以离开该页面稍后再来查看"
          style={{ width: '100%' }}
          indicator={
            <Progress
              percentage={progress}
              theme={'circle'}
              size={60}
            />}
        >
          <Space
            size={10}
            align='center'
            style={{ display: 'flex', width: '100%', height: '100%' }}
            className={styles.customSpace}
          >
            {urls.map((urlInfo, index) => {
              const { url, errMsg: err } = urlInfo;
              return (
                <Loading
                  key={index}
                  showOverlay
                  loading={!url && !err && !loading && historyStatus !== Status.FINISHED}
                  style={{ display: 'flex', flex: 1, height: '100%' }}
                >
                  <Tooltip content={err}>
                  <div
                    key={index}
                    className={classNames(styles.image, { [styles.selected]: isSelected(url) })}
                      onContextMenu={e => e.preventDefault()}
                      onClick={() => clickImage(urlInfo)}
                  >
                      {(historyStatus === Status.FINISHED || err) && !isValid(url)
                        ? <div className={styles.fail}>
                          <InfoCircleIcon />
                        </div>
                        : <>
                          <Image
                            src={url || Empty}
                            fit={'contain'}
                            loading={'...'}
                          />
                          {isValid(url) && <div
                            className={styles.review}
                            onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                          >
                            <BasicImageViewer
                              hover={false}
                              images={validUrls}
                              defaultIndex={validUrls.indexOf(url)}
                              handleDownload={v => downloadImageWatermark(v, isHorizontal)}
                            />
                          </div>}
                          {(isValid(url) && storyId)
                            && <Tooltip content={'保存到素材库'}>
                              <div className={styles.library}>
                                <FolderOpen1Icon
                                  color={'#FFFFFFE6'}
                                  onClick={e => saveLibrary(urlInfo, e)}
                                />
                              </div>
                            </Tooltip>}
                        </>}
                      {isSelected(url) && <CheckCircle className={styles.icon} />}
                  </div>
                  </Tooltip>
                </Loading>
              );
            })}
          </Space>
        </Loading>
      </div>
      <div className={styles.footer}>
        {style && <div className={styles.model}>
          <MissionIcon />
          <span>{style}</span>
        </div>}
        <div className={styles.buttons}>
          <Button
            theme="default"
            className={styles.btn}
            onClick={handleRegenerate}
          >
            <div className={styles.text}>
              <Reload />重新生成
            </div>
          </Button>
          <Button
            theme="default"
            className={styles.btn}
            ref={copyBtnRef}
          >
            <div className={styles.text}>
              <Copy3 />复制描述
            </div>
          </Button>
          <Popconfirm
            content={'删除生成记录'}
            onConfirm={handleDelete}
          >
            <Button
              theme="default"
              className={styles.delBtn}
              disabled={historyStatus !== Status.FINISHED}
            >
              <div className={styles.text}>
                <EditorDel />
              </div>
            </Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default ImageHistoryItem;
